import * as THREE from 'three';

// ─── canvas製の手続きテクスチャ集（外部画像に依存しない）───

function makeCanvas(w, h) {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  return canvas;
}

function toTexture(canvas, repeatX = 1, repeatY = 1) {
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(repeatX, repeatY);
  tex.anisotropy = 8;
  return tex;
}

// 乱数（シード固定で毎回同じ見た目にする）
function mulberry32(seed) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// 木の板張りの床
export function woodFloorTexture(repeatX = 4, repeatY = 3) {
  const canvas = makeCanvas(512, 512);
  const ctx = canvas.getContext('2d');
  const rand = mulberry32(11);

  const plankH = 512 / 8;
  for (let row = 0; row < 8; row++) {
    const y = row * plankH;
    // 板ごとの基調色（焦げ茶〜赤茶のゆらぎ）
    const base = 62 + rand() * 26;
    ctx.fillStyle = `rgb(${base + 28}, ${base - 4}, ${base - 30})`;
    ctx.fillRect(0, y, 512, plankH);

    // 木目の筋
    for (let i = 0; i < 42; i++) {
      const gy = y + rand() * plankH;
      const alpha = 0.05 + rand() * 0.1;
      ctx.strokeStyle = rand() > 0.5 ? `rgba(30, 18, 8, ${alpha})` : `rgba(215, 185, 140, ${alpha * 0.7})`;
      ctx.lineWidth = 0.6 + rand() * 1.4;
      ctx.beginPath();
      ctx.moveTo(0, gy);
      // うねる木目
      for (let x = 0; x <= 512; x += 64) {
        ctx.lineTo(x, gy + Math.sin(x * 0.02 + rand() * 6) * 2.5);
      }
      ctx.stroke();
    }

    // 節をたまに入れる
    if (rand() > 0.5) {
      const kx = rand() * 512;
      const ky = y + plankH * (0.3 + rand() * 0.4);
      const kr = 3 + rand() * 5;
      const g = ctx.createRadialGradient(kx, ky, 0, kx, ky, kr);
      g.addColorStop(0, 'rgba(28, 16, 8, 0.75)');
      g.addColorStop(1, 'rgba(28, 16, 8, 0)');
      ctx.fillStyle = g;
      ctx.fillRect(kx - kr, ky - kr, kr * 2, kr * 2);
    }

    // 板の継ぎ目
    ctx.fillStyle = 'rgba(20, 12, 6, 0.55)';
    ctx.fillRect(0, y, 512, 2);
    // 板の縦の切れ目（ランダム位置）
    const seams = 1 + Math.floor(rand() * 2);
    for (let s = 0; s < seams; s++) {
      ctx.fillRect(rand() * 512, y, 2, plankH);
    }
  }

  return toTexture(canvas, repeatX, repeatY);
}

// 漆喰壁（ざらっとした生成り）
export function plasterTexture(tint = '#ece5d8', repeatX = 3, repeatY = 1) {
  const canvas = makeCanvas(256, 256);
  const ctx = canvas.getContext('2d');
  const rand = mulberry32(7);

  ctx.fillStyle = tint;
  ctx.fillRect(0, 0, 256, 256);
  // まだら模様
  for (let i = 0; i < 900; i++) {
    const a = 0.015 + rand() * 0.035;
    ctx.fillStyle = rand() > 0.5 ? `rgba(120, 105, 80, ${a})` : `rgba(255, 252, 244, ${a})`;
    const r = 2 + rand() * 14;
    ctx.beginPath();
    ctx.arc(rand() * 256, rand() * 256, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // コテむらの斜めスジ
  for (let i = 0; i < 40; i++) {
    ctx.strokeStyle = `rgba(140, 125, 100, ${0.02 + rand() * 0.03})`;
    ctx.lineWidth = 4 + rand() * 10;
    const x = rand() * 256;
    const y = rand() * 256;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 40 + rand() * 60, y + (rand() - 0.5) * 30);
    ctx.stroke();
  }

  return toTexture(canvas, repeatX, repeatY);
}

// カウンター背面の黒板メニュー
export function menuBoardTexture() {
  const canvas = makeCanvas(512, 384);
  const ctx = canvas.getContext('2d');
  const rand = mulberry32(21);

  // 黒板
  ctx.fillStyle = '#20281f';
  ctx.fillRect(0, 0, 512, 384);
  // チョークのかすれ
  for (let i = 0; i < 300; i++) {
    ctx.fillStyle = `rgba(236, 229, 216, ${rand() * 0.03})`;
    ctx.fillRect(rand() * 512, rand() * 384, 1 + rand() * 3, 1 + rand() * 3);
  }

  ctx.textAlign = 'center';
  ctx.fillStyle = '#ece5d8';
  ctx.font = '30px "Cormorant Garamond", serif';
  ctx.fillText('— MENU —', 256, 56);

  ctx.font = '22px "Noto Sans JP", sans-serif';
  const items = [
    ['グアテマラハニー 150g', '¥1,400'],
    ['グアテマラSHB 150g', '¥1,000'],
    ['定期便 300g / 月', '¥2,520'],
  ];
  items.forEach(([name, price], i) => {
    const y = 120 + i * 52;
    ctx.textAlign = 'left';
    ctx.fillText(name, 48, y);
    ctx.textAlign = 'right';
    ctx.fillText(price, 464, y);
    ctx.strokeStyle = 'rgba(236, 229, 216, 0.25)';
    ctx.setLineDash([2, 4]);
    ctx.beginPath();
    ctx.moveTo(48, y + 12);
    ctx.lineTo(464, y + 12);
    ctx.stroke();
    ctx.setLineDash([]);
  });

  ctx.textAlign = 'center';
  ctx.fillStyle = '#b89d5a';
  ctx.font = '17px "Noto Sans JP", sans-serif';
  ctx.fillText('人生の余白時間を、コーヒーとともに。', 256, 330);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

// 湯気スプライト用のぼんやり円
export function steamTexture() {
  const canvas = makeCanvas(64, 64);
  const ctx = canvas.getContext('2d');
  const g = ctx.createRadialGradient(32, 32, 2, 32, 32, 30);
  g.addColorStop(0, 'rgba(255, 255, 255, 0.55)');
  g.addColorStop(0.5, 'rgba(255, 255, 255, 0.18)');
  g.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(canvas);
}

// 九谷焼風の絵付けカップ用パターン（五彩: 緑・黄・紫・紺・赤）
export function kutaniTexture(seed = 1) {
  const canvas = makeCanvas(128, 64);
  const ctx = canvas.getContext('2d');
  const rand = mulberry32(seed * 97);

  ctx.fillStyle = '#f6f1e6';
  ctx.fillRect(0, 0, 128, 64);
  const colors = ['#2e7d4f', '#d9a514', '#6a4a8c', '#274a8a', '#b03a2e'];
  for (let i = 0; i < 26; i++) {
    ctx.fillStyle = colors[Math.floor(rand() * colors.length)];
    const x = rand() * 128;
    const y = rand() * 64;
    if (rand() > 0.5) {
      ctx.beginPath();
      ctx.arc(x, y, 2 + rand() * 5, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.fillRect(x, y, 3 + rand() * 8, 2 + rand() * 4);
    }
  }
  // 上下の縁取り
  ctx.fillStyle = '#274a8a';
  ctx.fillRect(0, 0, 128, 5);
  ctx.fillRect(0, 59, 128, 5);

  return toTexture(canvas, 1, 1);
}
